import { DEMO_BOARD_MAP_IDS, mapsFromCatalog } from '../maps'
import type { ScenarioObject, ScenarioSession } from '../types'

export const EXAMPLE_WHITEBOARD_ID = 'example-whiteboard'

/** Silent technical origin for internal story math — whiteboard has no timed story. */
const ORIGIN = '2026-09-22T10:00:00-05:00'

/** Bare-geography center (same plate framing as blank whiteboard). */
const CAM = { lng: -115.478, lat: 33.852, zoom: 16.2 }

/** 2×2 teaching tiles around the camera center. */
const Q1 = { lng: -115.4796, lat: 33.8533 } // NW — mark a hazard
const Q2 = { lng: -115.4764, lat: 33.8533 } // NE — establish incident facilities
const Q3 = { lng: -115.4796, lat: 33.8507 } // SW — draw isolation zones
const Q4 = { lng: -115.4764, lat: 33.8507 } // SE — place response

function pin(
  id: string,
  type: string,
  label: string,
  pose: { lng: number; lat: number },
  extras?: Partial<ScenarioObject>
): ScenarioObject {
  return {
    id,
    type,
    label,
    mapLabel: extras?.mapLabel ?? label,
    scope: 'local',
    pose,
    size: extras?.size ?? 'md',
    description: extras?.description,
    showOnTimeline: false,
    params: extras?.params ?? {},
    comments: [],
    ...extras,
  }
}

/**
 * Kitchen-sink whiteboard: four small usage ideas in a 2×2 layout on Bare geography.
 * Rough-in for trainers — not a timed exercise.
 */
export function createExampleWhiteboardSession(): ScenarioSession {
  return {
    id: EXAMPLE_WHITEBOARD_ID,
    title: 'Example whiteboard',
    intro: 'Four quick whiteboard ideas on one board — hazard, command, zones, response.',
    summary:
      'Kitchen-sink whiteboard: mark a hazard, establish incident facilities, draw isolation zones, place response.',
    storyStartTime: ORIGIN,
    timezone: 'America/Chicago',
    showTimeZone: false,
    displayUnits: 'imperial',
    boardComplexity: 'everything',
    sessionType: 'whiteboard',
    maps: mapsFromCatalog(DEMO_BOARD_MAP_IDS),
    activeMapId: 'bare-geography',
    camera: { ...CAM },
    comments: [],
    timeline: [],
    objects: [
      // — Q1: Mark a hazard —
      pin('ex-q1-title', 'label', '1 · Mark a hazard', { lng: Q1.lng, lat: Q1.lat + 0.00055 }, {
        params: { text: '1 · Mark a hazard', color: '#ffffff' },
        size: 'lg',
      }),
      pin('ex-q1-fire', 'fire', 'Fire', { lng: Q1.lng - 0.00025, lat: Q1.lat }, {
        description: 'Drop a hazard pin where the problem is.',
      }),
      pin('ex-q1-hot', 'hot_zone', 'Hot zone', { lng: Q1.lng + 0.0002, lat: Q1.lat - 0.00015 }, {
        description: 'Ring the keep-out around the hazard.',
        params: { radiusM: 45 },
        size: 'lg',
      }),

      // — Q2: Establish incident facilities —
      pin(
        'ex-q2-title',
        'label',
        '2 · Establish incident facilities',
        { lng: Q2.lng, lat: Q2.lat + 0.00055 },
        {
          params: { text: '2 · Establish incident facilities', color: '#ffffff' },
          size: 'lg',
        },
      ),
      pin('ex-q2-icp', 'icp', 'ICP', { lng: Q2.lng - 0.0003, lat: Q2.lat }, {
        description: 'Incident command post.',
      }),
      pin('ex-q2-dispatch', 'dispatch', 'Dispatch', { lng: Q2.lng + 0.00005, lat: Q2.lat + 0.0001 }, {
        description: 'Dispatch / communications.',
      }),
      pin('ex-q2-staging', 'staging', 'Staging', { lng: Q2.lng + 0.0003, lat: Q2.lat - 0.00015 }, {
        description: 'Apparatus and crew staging.',
      }),

      // — Q3: Draw isolation zones —
      pin(
        'ex-q3-title',
        'label',
        '3 · Draw isolation zones',
        { lng: Q3.lng, lat: Q3.lat + 0.00055 },
        {
          params: { text: '3 · Draw isolation zones', color: '#ffffff' },
          size: 'lg',
        },
      ),
      pin('ex-q3-iso', 'isolation', 'Isolation', { lng: Q3.lng - 0.0002, lat: Q3.lat }, {
        description: 'Isolation area for planning.',
        params: { radiusM: 55 },
        size: 'lg',
      }),
      pin('ex-q3-evac', 'evacuate', 'Evacuation', { lng: Q3.lng + 0.00025, lat: Q3.lat - 0.0001 }, {
        description: 'Evacuation zone sketch.',
        params: { radiusM: 70 },
        size: 'lg',
      }),

      // — Q4: Place response —
      pin('ex-q4-title', 'label', '4 · Place response', { lng: Q4.lng, lat: Q4.lat + 0.00055 }, {
        params: { text: '4 · Place response', color: '#ffffff' },
        size: 'lg',
      }),
      pin('ex-q4-engine', 'engine', 'Engine', { lng: Q4.lng - 0.0003, lat: Q4.lat }, {
        description: 'Engine company on the board.',
        size: 'lg',
      }),
      pin('ex-q4-ems', 'ems', 'EMS', { lng: Q4.lng + 0.00005, lat: Q4.lat - 0.00005 }, {
        description: 'EMS / ambulance.',
        size: 'lg',
      }),
      pin('ex-q4-road', 'road', 'Road', { lng: Q4.lng + 0.0003, lat: Q4.lat + 0.00005 }, {
        description: 'Road status (open / slow / closed).',
      }),
    ],
  }
}
